import { useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Wifi, WifiOff, Globe2, MapPin, RefreshCw, Shield, ShieldAlert } from "lucide-react";

const SITE_LABEL = { SITE_1: "Christchurch", SITE_2: "Wellington" };

function Pill({ tone, icon: Icon, children }) {
  const tones = {
    ok: "bg-emerald-100 text-emerald-800 border-emerald-200",
    warn: "bg-amber-100 text-amber-800 border-amber-200",
    err: "bg-red-100 text-red-800 border-red-200",
    info: "bg-[#1A2744]/5 text-[#1A2744] border-[#1A2744]/10",
  };
  return (
    <Badge variant="outline" className={`text-[11px] gap-1 ${tones[tone] || tones.info}`}>
      {Icon && <Icon className="h-3 w-3" strokeWidth={1.8} />}
      {children}
    </Badge>
  );
}

export function LocationCheckCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/ip-check");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Check failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const country = data?.country;
  const countryOk = country === "NZ" || country === "LOCAL";
  const wifi = data?.on_cafe_wifi;
  const enforcement = data?.ip_restriction_enabled;

  return (
    <Card className="border-gray-200" data-testid="location-check-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9A84C] font-semibold">Troubleshooting</div>
            <h3 className="font-heading text-xl font-bold text-[#1A2744] mt-1">Test my current location</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-md">
              Ask a staff member in the café to open this page on their device — if checks pass, they can clock in.
            </p>
          </div>
          <Button
            size="sm"
            className="bg-[#1A2744] text-white hover:bg-[#263A63] shrink-0"
            onClick={run}
            disabled={loading}
            data-testid="run-ip-check"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            {data ? "Re-run check" : "Run check"}
          </Button>
        </div>

        {error && (
          <div className="mt-4 text-xs text-red-600" data-testid="ip-check-error">{error}</div>
        )}

        {data && !error && (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-3" data-testid="ip-check-ip">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1">
                <MapPin className="h-3 w-3 text-[#C9A84C]" strokeWidth={1.8} /> Public IP
              </div>
              <div className="font-mono text-sm text-[#1A2744] mt-1 truncate" title={data.ip}>{data.ip || "—"}</div>
              {data.x_forwarded_for && (
                <div className="text-[10px] text-gray-400 mt-1 truncate" title={data.x_forwarded_for}>XFF: {data.x_forwarded_for}</div>
              )}
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-3" data-testid="ip-check-country">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1">
                <Globe2 className="h-3 w-3 text-[#C9A84C]" strokeWidth={1.8} /> Country
              </div>
              <div className="text-sm text-[#1A2744] mt-1 font-medium">
                {country === "LOCAL" ? "Local network" : (country || "Unknown")}
              </div>
              <div className="mt-1.5">
                {countryOk ? (
                  <Pill tone="ok" icon={Shield}>Staff portal OK</Pill>
                ) : (
                  <Pill tone="err" icon={ShieldAlert}>Staff portal blocked</Pill>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-3" data-testid="ip-check-wifi">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1">
                {wifi ? <Wifi className="h-3 w-3 text-[#C9A84C]" strokeWidth={1.8} /> : <WifiOff className="h-3 w-3 text-[#C9A84C]" strokeWidth={1.8} />}
                Café Wi-Fi
              </div>
              <div className="text-sm text-[#1A2744] mt-1 font-medium">
                {wifi ? "On café network" : enforcement ? "Not on café Wi-Fi" : "Not on café Wi-Fi"}
              </div>
              <div className="mt-1.5">
                {wifi ? (
                  <Pill tone="ok" icon={Wifi}>Clock-in OK</Pill>
                ) : enforcement ? (
                  <Pill tone="err" icon={WifiOff}>Clock-in blocked</Pill>
                ) : (
                  <Pill tone="warn" icon={Wifi}>Warn-only (not enforced)</Pill>
                )}
              </div>
            </div>
          </div>
        )}

        {data && !error && (
          <div className="mt-4 flex flex-wrap gap-2 text-[11px]" data-testid="ip-check-status">
            <Pill tone={data.geoip_loaded ? "ok" : "warn"}>
              GeoIP DB: {data.geoip_loaded ? "loaded" : "missing"}
            </Pill>
            <Pill tone={enforcement ? "ok" : "warn"}>
              Wi-Fi enforcement: {enforcement ? "on" : "off (warn-only)"}
            </Pill>
            <Pill tone="info">
              Allowed countries: {(data.allowed_countries || []).join(", ") || "—"}
            </Pill>
          </div>
        )}

        {!data && !error && !loading && (
          <p className="mt-4 text-xs text-gray-400">
            Click <span className="font-medium text-[#1A2744]">Run check</span> to diagnose.
            Tip: ask the affected staff member to open this page on their phone while on the café Wi-Fi.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export { SITE_LABEL };
